import { Link } from "react-router-dom";
import { NerdStackFooter } from "../components/NerdStackFooter.jsx";
import { ToolCard } from "../components/ToolCard.jsx";
import { FolderGrid } from "../components/FolderGrid.jsx";
import BackArrow from "../components/BackArrow.jsx";
import { PhysShell, PHYS_ACCENT, VIOLET } from "./PhysShell.jsx";

// V45.3 — PhysHome is now a custom ChemHome-style layout (not a PhysShell wrapper).
const EM = PHYS_ACCENT;     // cyan #22B8F0
const EM_DEEP = VIOLET;
const EM_LIGHT = "#7DDEFA"; // light cyan for gradient-text

const rgba = (hex, a) => {
  const r = parseInt(hex.slice(1, 3), 16), g = parseInt(hex.slice(3, 5), 16), b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${a})`;
};

// PhysLanding — preserved verbatim from pre-V45.3.
export function PhysLanding() {
  return (
    <div style={{ minHeight: "100vh", background: "#0A0B14", color: "#E8E9F3", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24, textAlign: "center", paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}>
      <div style={{ width: 72, height: 72, borderRadius: 18, background: `linear-gradient(135deg, ${PHYS_ACCENT}, ${VIOLET})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, fontWeight: 700, color: "#fff", marginBottom: 20 }}>
        Ph
      </div>
      <h1 style={{ fontSize: 26, fontWeight: 700, margin: "0 0 8px" }}>NerdPhys</h1>
      <p style={{ fontSize: 15, color: "#9A9DBE", margin: "0 0 28px", maxWidth: 320 }}>
        Projectile motion, kinematics, forces, energy. Free. Fast. Actually good.
      </p>
      <Link to="/phys/home" style={{ background: PHYS_ACCENT, color: "#02232E", fontWeight: 600, fontSize: 15, padding: "13px 28px", borderRadius: 12, textDecoration: "none" }}>
        Open NerdPhys
      </Link>
    </div>
  );
}

export function PhysHome() {
  return (
    <div style={{ minHeight: "100vh", background: "#0A0B14", fontFamily: "'DM Sans', system-ui, sans-serif", color: "#E2E8F0" }}>
      <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, height: "70%", background: `radial-gradient(ellipse at center bottom, ${rgba(EM, 0.18)} 0%, transparent 70%)`, pointerEvents: "none" }} />

      <header style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "16px 24px",
        paddingTop: "calc(16px + env(safe-area-inset-top))",
        paddingLeft: "calc(24px + env(safe-area-inset-left))",
        paddingRight: "calc(24px + env(safe-area-inset-right))",
        position: "relative", zIndex: 10,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <BackArrow to="/" from={EM} toColor={EM_LIGHT} />
          <div style={{ width: 38, height: 38, borderRadius: 11, background: `linear-gradient(135deg, ${EM}, ${EM_DEEP})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17, fontWeight: 800, color: "#fff" }}>Ph</div>
          <svg width="105" height="24" viewBox="0 0 105 24" xmlns="http://www.w3.org/2000/svg" style={{ display: "block" }}>
            <defs>
              <linearGradient id="wm-phys" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stopColor={EM_LIGHT} />
                <stop offset="1" stopColor="#7DDEFA" />
              </linearGradient>
            </defs>
            <text x="0" y="19" fill="url(#wm-phys)" fontFamily="DM Sans, system-ui, sans-serif" fontSize="20" fontWeight="800" letterSpacing="-0.5">NerdPhys</text>
          </svg>
        </div>
      </header>

      <main style={{ maxWidth: 820, margin: "0 auto", padding: "40px 20px 80px", position: "relative", zIndex: 10 }}>
        <h1 style={{ fontSize: "clamp(32px, 6vw, 52px)", fontWeight: 800, letterSpacing: "-1.5px", textAlign: "center", margin: "0 0 14px", lineHeight: 1.1 }}>
          Physics tools.<br /><span style={{ background: `linear-gradient(135deg, ${EM_LIGHT}, ${EM})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Free. Fast. Actually good.</span>
        </h1>
        <p style={{ fontSize: 17, color: "#9CA3AF", textAlign: "center", maxWidth: 580, margin: "0 auto 14px", lineHeight: 1.6 }}>
          Physics by what it does, not what it says. Projectile motion, kinematics, force diagrams, energy and momentum — formulas with context, not flashcards.
        </p>
        <p style={{ fontSize: 15, color: "#6F7290", textAlign: "center", maxWidth: 580, margin: "0 auto 44px", lineHeight: 1.6 }}>
          Free. No signup. No dark patterns.
        </p>

        <FolderGrid ariaLabel="Physics tools">
          <ToolCard accent={EM} to="/phys/projectile" name="Projectile Motion" desc="Range, apex, flight time + live trajectory plot" signature />
          <ToolCard accent={EM} to="/phys/kinematics" name="Kinematics (SUVAT)" desc="Solve constant-acceleration motion from any 3 values" />
          <ToolCard accent={EM} to="/phys/force" name="Newton's 2nd Law" desc="F = ma — solve for the missing quantity" />
          <ToolCard accent={EM} to="/phys/energy" name="Energy & Work" desc="Kinetic, potential, and work done by a force" />
          <ToolCard accent={EM} to="/phys/ohms" name="Ohm's Law" desc="V = IR with power — solve any unknown" />
          <ToolCard accent={EM} to="/phys/particles" name="Particle Reference" desc="The Standard Model — 17 particles, filterable" />
          <ToolCard accent={EM} to="/phys/waves" name="Wave Calculator" desc="v = f λ — speed, frequency, wavelength, period" />
          <ToolCard accent={EM} to="/phys/momentum" name="Momentum & Collisions" desc="1D collisions, elastic to inelastic, KE loss" />
          <ToolCard accent={EM} to="/phys/reference" name="Physics Reference" desc="Concepts, equations, worked examples — kinematics through QFT" />
          <ToolCard accent={EM} to="/suggest" name="Suggest a tool" desc="Missing something? Tell us what to build next." />
        </FolderGrid>
      </main>

      <footer style={{ textAlign: "center", padding: 24, fontSize: 12, color: "#1F2937", borderTop: `1px solid ${rgba(EM, 0.04)}` }}>
        <NerdStackFooter variant="tool" family="phys" />
      </footer>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; }
        body { margin: 0; }
      `}</style>
    </div>
  );
}
